import { Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NumberInput } from "@/components/ui/number-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatCurrency } from "@/lib/utils";

import {
  hasBarQty,
  hasEnteredCost,
  hasWarehouseQty,
  isIngredient,
  itemTypeLabel,
  type FormLine,
} from "./opening-stock-utils";

type Field = "qtyOnHand" | "qtyRepacking" | "unitCost";

interface LinesProps {
  lines: FormLine[];
  locked: boolean;
  onChange: (index: number, field: Field, value: number) => void;
  onRemove: (index: number) => void;
}

function QtyField({
  line,
  field,
  locked,
  onChange,
}: {
  line: FormLine;
  field: "qtyOnHand" | "qtyRepacking";
  locked: boolean;
  onChange: (value: number) => void;
}) {
  const applies =
    field === "qtyOnHand" ? hasWarehouseQty(line) : hasBarQty(line);
  if (!applies) return <span className="text-muted-foreground">—</span>;
  if (locked) return <span className="font-medium">{line[field]}</span>;
  return (
    <NumberInput
      value={line[field]}
      onChange={(v) => onChange(v ?? 0)}
      min={0}
      className="h-8 w-24 text-xs"
    />
  );
}

function CostField({
  line,
  locked,
  onChange,
}: {
  line: FormLine;
  locked: boolean;
  onChange: (value: number) => void;
}) {
  if (hasEnteredCost(line) && !locked) {
    return (
      <div className="flex items-center gap-1.5">
        <NumberInput
          value={line.unitCost}
          onChange={(v) => onChange(v ?? 0)}
          min={0}
          className="h-8 w-32 text-xs"
        />
        {/* The price is per the line's own unit, not per pack it was bought in. */}
        <span className="whitespace-nowrap text-[10px] text-muted-foreground">
          / {line.uomName}
        </span>
      </div>
    );
  }
  if (isIngredient(line) && line.unitCost <= 0) {
    return (
      <Badge variant="destructive" className="text-[10px]">
        Tambahkan barang beli pasangannya
      </Badge>
    );
  }
  return (
    <span>
      <span className="font-medium">{formatCurrency(line.unitCost)}</span>
      {!hasEnteredCost(line) && (
        <span className="block text-[10px] text-muted-foreground">
          {isIngredient(line) ? "Dari barang beli" : "Dari resep"}
        </span>
      )}
    </span>
  );
}

export function OpeningStockLines({
  lines,
  locked,
  onChange,
  onRemove,
}: LinesProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <div className="divide-y divide-border">
        {lines.map((line, idx) => (
          <div key={line.itemId} className="space-y-3 p-4 text-xs">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold">{line.itemName}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                  <Badge variant="outline" className="px-1 py-0 text-[10px]">
                    {itemTypeLabel(line)}
                  </Badge>
                  <span>{line.uomName}</span>
                </div>
              </div>
              {!locked && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 text-destructive"
                  onClick={() => onRemove(idx)}
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {(["qtyOnHand", "qtyRepacking"] as const).map((field) => (
                <div key={field} className="space-y-1">
                  <Label className="text-[10px] text-muted-foreground">
                    {field === "qtyOnHand" ? "Stok Gudang" : "Stok Bar"}
                  </Label>
                  <div>
                    <QtyField
                      line={line}
                      field={field}
                      locked={locked}
                      onChange={(v) => onChange(idx, field, v)}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-end justify-between border-t border-border/50 pt-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-muted-foreground">
                  Harga per {line.uomName}
                </Label>
                <CostField
                  line={line}
                  locked={locked}
                  onChange={(v) => onChange(idx, "unitCost", v)}
                />
              </div>
              <div className="text-right">
                <span className="block text-[10px] text-muted-foreground">
                  Subtotal
                </span>
                <span className="font-bold">
                  {formatCurrency(line.subtotalCost)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="text-xs">
          <TableHead className="w-10">No</TableHead>
          <TableHead>Barang</TableHead>
          <TableHead className="w-24">Satuan</TableHead>
          <TableHead className="w-28">Gudang</TableHead>
          <TableHead className="w-28">Bar</TableHead>
          <TableHead className="w-44">Harga per Satuan</TableHead>
          <TableHead className="w-32 text-right">Subtotal</TableHead>
          {!locked && <TableHead className="w-12" />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {lines.map((line, idx) => (
          <TableRow key={line.itemId} className="text-xs">
            <TableCell>{idx + 1}</TableCell>
            <TableCell>
              <div className="font-medium">{line.itemName}</div>
              <div className="mt-0.5 flex items-center gap-1.5">
                {line.itemSku && (
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {line.itemSku}
                  </span>
                )}
                <Badge variant="outline" className="px-1 py-0 text-[10px]">
                  {itemTypeLabel(line)}
                </Badge>
              </div>
            </TableCell>
            <TableCell className="text-muted-foreground">
              {line.uomName}
            </TableCell>
            {(["qtyOnHand", "qtyRepacking"] as const).map((field) => (
              <TableCell key={field}>
                <QtyField
                  line={line}
                  field={field}
                  locked={locked}
                  onChange={(v) => onChange(idx, field, v)}
                />
              </TableCell>
            ))}
            <TableCell>
              <CostField
                line={line}
                locked={locked}
                onChange={(v) => onChange(idx, "unitCost", v)}
              />
            </TableCell>
            <TableCell className="text-right font-semibold">
              {formatCurrency(line.subtotalCost)}
            </TableCell>
            {!locked && (
              <TableCell>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-destructive hover:text-destructive"
                  onClick={() => onRemove(idx)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
